// Copyright (c) Microsoft. All rights reserved.
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';
import { useDispatch } from 'react-redux';
import { IChatMessage } from '../../../../../libs/models/ChatMessage';
import { setNotifications } from '../../../../../redux/features/app/appSlice';
import { copyUrlToClipboard } from '../../../../utils/CopyUtils';
import { getCurrentHost } from '../../../../utils/UrlUtils';
import { NOTIFICATION_TEMPLATES } from '../../Notifications/NotificationConstants';
import { ModalDialog } from '../Shared/ModalDialog';

interface IPromptDialogProps {
    message: IChatMessage;
    open: boolean;
    onClose: () => void;
}

export const QRDialog: React.FC<IPromptDialogProps> = ({ open, onClose, message }) => {
    const [qrValue, setQrValue] = useState('');
    const [urlCopied, setUrlCopied] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (message && message.id && message.chatId) {
            const host = getCurrentHost();
            setQrValue(`${host}?chatId=${message.chatId}&messageId=${message.id}`);
        }
    }, [message]);

    console.log(qrValue);

    return (
        <>
            <ModalDialog title="Message links" open={open} onClose={onClose}>
                <div className="flex flex-col gap-4 mt-3 text-center sm:mt-0 sm:text-left text-sm">
                    <div>
                        <span>Copy this link to return to this message</span>
                    </div>
                    <div className="flex items-center border border-gray-600 p-2 rounded-md dark:bg-slate-700">
                        <div className="relative w-full">
                            <input
                                className="w-full bg-transparent pr-4 p-2 focus:outline focus:outline-blue-300 rounded-md"
                                value={qrValue}
                                readOnly
                            />
                            <div className="absolute top-0 right-0 h-full w-20 pointer-events-none bg-gradient-to-r rounded-r-md from-transparent dark:to-slate-700 to-white"></div>
                        </div>
                        <button
                            type="button"
                            data-autofocus
                            onClick={() => {
                                void copyUrlToClipboard(qrValue, setUrlCopied);
                                dispatch(setNotifications([{ ...NOTIFICATION_TEMPLATES.COPY_URL_CHATIMAGE }]));
                                onClose();
                            }}
                            className="inline-flex items-center ml-2 w-60 cursor-pointer justify-center rounded-lg py-2 text-white bg-primary-600 hover:bg-primary-700 dark:text-slate-50 transition-all duration-300">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                                stroke={urlCopied ? '#fff' : 'currentColor'}
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <path d="M8 8m0 2a2 2 0 0 1 2 -2h8a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-8a2 2 0 0 1 -2 -2z"></path>
                                <path d="M16 8v-2a2 2 0 0 0 -2 -2h-8a2 2 0 0 0 -2 2v8a2 2 0 0 0 2 2h2"></path>
                            </svg>
                            <span className="ml-2">Copy link</span>
                        </button>
                    </div>
                    <div>
                        <span>Scan the QR code to continue this conversation on your device</span>
                    </div>
                    <div className="w-full flex justify-center mt-2 mb-2">
                        {qrValue && (
                            <QRCode
                                className="bg-white border-2 border-solid border-black dark:border-none p-4 rounded-md"
                                value={qrValue}
                            />
                        )}
                    </div>
                    <div className="flex items-center justify-center text-sm">
                        <InformationCircleIcon className="w-4 h-4" />
                        <span className="ml-2 text-sm">Please note these links are private and cannot be shared</span>
                    </div>
                </div>
            </ModalDialog>
        </>
    );
};
